/* Helper utilities to parse and format per-lambda summaries.
 * Many summaries arrive as double-encoded JSON strings. These helpers
 * will robustly decode to a plain object and provide a normalized view.
 */

export type ParsedSummary = Record<string, unknown>;

// Try to decode JSON that may be encoded multiple times or have escaped quotes
export function decodePossiblyDoubleEncodedJson(input: unknown): unknown {
  let val: unknown = input;
  // If value is an array-like string (e.g., '["x","y"]'), let the caller handle lists.
  for (let i = 0; i < 3; i++) {
    if (typeof val === "string") {
      const s = val.trim();
      if (!s) return s;
      try {
        val = JSON.parse(s);
      } catch {
        // Attempt to unescape common over-escaped quotes
        try {
          // 1) Replace escaped quotes
          let deEscaped = s.replace(/\\"/g, '"');
          // 2) Replace doubled quotes often found in double-encoded JSON
          deEscaped = deEscaped.replace(/""/g, '"');
          val = JSON.parse(deEscaped);
        } catch {
          // Stop trying if we cannot parse further
          // If it still looks like JSON with doubled quotes, last-ditch cleanup:
          if (s.startsWith('"') && s.endsWith('"')) {
            const inner = s.slice(1, -1).replace(/""/g, '"');
            try {
              val = JSON.parse(inner);
              continue;
            } catch {
              // fall through
            }
          }
          return s;
        }
      }
    }
  }
  return val;
}

// Lambda-specific normalizers
function normalizeMergeAudioChunksSummary(raw: any): ParsedSummary | null {
  if (!raw || typeof raw !== "object") return null;
  return {
    conversation_id: raw.conversation_id ?? "",
    merge_type: raw.merge_type ?? "",
    num_chunks: raw.num_chunks ?? "",
    audio_duration_seconds: raw.audio_duration_seconds ?? "",
    merged_audio_s3_url: raw.merged_audio_s3_url ?? "",
    output_s3_bucket: raw.output_s3_bucket ?? "",
  };
}

function normalizeICD10VASummary(raw: any): ParsedSummary | null {
  if (!raw || typeof raw !== "object") return null;
  const codes = Array.isArray(raw.codes) ? raw.codes : [];
  return {
    // ICD10_VA legacy format (no platform)
    summarize_job_name: raw.summarize_job_name ?? "",
    total_codes: raw.total_codes ?? codes.length,
    codes,
  };
}

function normalizeICD10v2Summary(raw: any): ParsedSummary | null {
  if (!raw || typeof raw !== "object") return null;
  const codes = Array.isArray(raw.codes) ? raw.codes : [];
  const calls = Array.isArray(raw.llm_calls) ? raw.llm_calls : [];
  return {
    platform: raw.platform ?? "",
    summarize_job_name: raw.summarize_job_name ?? "",
    total_codes: raw.total_codes ?? codes.length,
    codes,
    // expose individual calls (unaggregated) so the table can render each
    llm_calls: calls,
  };
}

function normalizeWhisperXDiarizationSummary(raw: any): ParsedSummary | null {
  if (!raw || typeof raw !== "object") return null;
  return {
    transcribe_job_name: raw.transcribe_job_name ?? "",
    transcript_language: raw.transcript_language ?? "",
    num_segments: raw.num_segments ?? "",
  };
}

function normalizeRunOrdersV2Summary(raw: any): ParsedSummary | null {
  if (!raw || typeof raw !== "object") return null;
  return {
    summarize_job_name: raw.summarize_job_name ?? "",
    super_org: raw.super_org ?? "",
    orders_count: raw.orders_count ?? "",
    // expose llm_calls list verbatim for per-call UI rendering
    llm_calls: Array.isArray(raw.llm_calls) ? raw.llm_calls : [],
    // keep legacy fields if they appear
    model_used: raw.model_used ?? "",
    input_tokens: raw.input_tokens ?? "",
    output_tokens: raw.output_tokens ?? "",
  };
}

function normalizeRunMacrosSummary(raw: any): ParsedSummary | null {
  if (!raw || typeof raw !== "object") return null;
  return {
    summarize_job_name: raw.summarize_job_name ?? "",
    specialty: raw.specialty ?? "",
    macros_applied: raw.macros_applied ?? "",
    llm_calls: Array.isArray(raw.llm_calls) ? raw.llm_calls : [],
  };
}

function normalizeRunEvaluationManagementSummary(raw: any): ParsedSummary | null {
  if (!raw || typeof raw !== "object") return null;
  return {
    summarize_job_name: raw.summarize_job_name ?? "",
    e_and_m_code: raw.e_and_m_code ?? "",
    visit_type: raw.visit_type ?? "",
    llm_calls: Array.isArray(raw.llm_calls) ? raw.llm_calls : [],
  };
}

function normalizeRunUpdateDiarizationResultsSummary(raw: any): ParsedSummary | null {
  if (!raw || typeof raw !== "object") return null;
  return {
    transcribe_job_name: raw.transcribe_job_name ?? "",
    diarization_status: raw.diarization_status ?? "",
    processing_time_ms: raw.processing_time_ms ?? "",
  };
}

export function parseSummary(lambdaName: string, rawSummary: unknown): ParsedSummary | null {
  // First decode if it's a double-encoded JSON string
  const decoded = decodePossiblyDoubleEncodedJson(rawSummary);
  // If it parsed into an array, wrap to a single object for display
  // (callers can still display list fields as needed).
  const obj = Array.isArray(decoded) ? { items: decoded } : decoded;

  // Route by lambda name
  switch (lambdaName) {
    case "MergeAudioChunksFunction":
      return normalizeMergeAudioChunksSummary(obj);
    case "ICD10_VA":
      return normalizeICD10VASummary(obj);
    case "ICD10_v2":
      return normalizeICD10v2Summary(obj);
    case "UploadConversationFunction":
      // UploadConversationFunction is already a flat object with a few known keys
      if (obj && typeof obj === "object") {
        const raw: any = obj;
        return {
          conversation_id: raw.conversation_id ?? "",
          pipeline: raw.pipeline ?? "",
          platform: raw.platform ?? "",
        };
      }
      return null;
    case "RunTranscriptionFunction":
      if (obj && typeof obj === "object") {
        const raw: any = obj;
        return {
          platform: raw.platform ?? "",
          conversation_id: raw.conversation_id ?? "",
          s3_source_key: raw.s3_source_key ?? "",
          service_used: raw.service_used ?? "",
          audio_length_seconds: raw.audio_length_seconds ?? "",
          transcription_duration_ms: raw.transcription_duration_ms ?? "",
          job_name: raw.job_name ?? "",
          transcript_url: raw.transcript_url ?? "",
          request_id: raw.request_id ?? "",
        };
      }
      return null;
    case "RunChatgptFunction":
      if (obj && typeof obj === "object") {
        const raw: any = obj;
        return {
          platform: raw.platform ?? "",
          job_name: raw.job_name ?? "",
          summarize_job_name: raw.summarize_job_name ?? "",
          note_length_chars: raw.note_length_chars ?? "",
          llm_calls: Array.isArray(raw.llm_calls) ? raw.llm_calls : [],
        };
      }
      return null;
    case "RunKnowtexWhisperXDiarizationFunction":
      return normalizeWhisperXDiarizationSummary(obj);
    case "RunOrdersV2Function":
      return normalizeRunOrdersV2Summary(obj);
    case "RunMacrosFunction":
      return normalizeRunMacrosSummary(obj);
    case "RunEvaluationManagementFunction":
      return normalizeRunEvaluationManagementSummary(obj);
    case "RunUpdateDiarizationResultsFunction":
      return normalizeRunUpdateDiarizationResultsSummary(obj);
    case "MobileAudioProcessorFunction":
      if (obj && typeof obj === "object") {
        const raw: any = obj;
        const svc = raw.service_used || {};
        const chunks = Array.isArray(svc.chunks) ? svc.chunks : [];
        return {
          platform: raw.platform ?? "",
          conversation_id: raw.conversation_id ?? "",
          job_name: raw.job_name ?? "",
          audio_length_seconds: raw.audio_length_seconds ?? "",
          transcription_duration_ms: raw.transcription_duration_ms ?? "",
          transcript_language: raw.transcript_language ?? "",
          transcript_length_chars: raw.transcript_length_chars ?? "",
          service_whisper_total: svc.whisper_total ?? "",
          service_deepgram_total: svc.deepgram_total ?? "",
          service_chunks_count: chunks.length,
        };
      }
      return null;
    default:
      // Fallback: if object-like, return as-is; otherwise null
      if (obj && typeof obj === "object") return obj as ParsedSummary;
      return null;
  }
}

